import {Button, Dropdown, DropdownItem, useTranslationContext} from 'platform/components';

import {isNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import type {GridDataChange} from '../../types/GridDataChange';
import {getBulkActionsFromRows} from '../../utils/getBulkActionsFromRows';
import {invokeDatagridBulkAction} from '../../utils/invokeDatagridBulkAction';
import {isGridDataLoading} from '../../utils/isGridDataLoading';
import {toRegisteredIcon} from '../../utils/toRegisteredIcon';

/**
 * Mutually exclusive narrowing of the rendered action set — pass an allow-list OR a
 * deny-list, never both (`never` on the counterpart makes mixing a compile error).
 */
type ActionFilterProps =
  | {
      /**
       * Allow-list of action keys: when set, only these bulk actions render (in addition
       * to the grid's own bulkAction metadata — a key listed here but not
       * flagged/applicable still does not render).
       */
      allowedActions?: string[];
      removedActions?: never;
    }
  | {
      /** Deny-list of action keys that never render. */
      removedActions?: string[];
      allowedActions?: never;
    };

type ExternalBulkActionsBarProps = TestIdProps &
  ActionFilterProps & {
    /** Same gridCode the EagGrid was given — the invoke channel is keyed by it. */
    gridCode: string;
    /** The GridDataChange the host received via onGridDataChange (null before first load). */
    data: GridDataChange | null;
    /**
     * Label of the dropdown trigger button. Pass an already-translated string;
     * defaults to the translated "Bulk actions".
     */
    label?: string;
  };

/**
 * Renders a datagrid's bulk actions OUTSIDE the grid, derived entirely from the grid's
 * own action metadata (no per-consumer action config). A single "Bulk actions" dropdown
 * button whose menu holds one item per bulkAction-flagged action, labelled with the
 * count of loaded rows it applies to — items with a zero count stay visible but
 * disabled; clicking an item invokes the action on the grid via
 * invokeDatagridBulkAction. The rendered set can be narrowed per consumer via
 * allowedActions (allow-list) or removedActions (deny-list) — mutually exclusive.
 * Renders nothing while data is unavailable or the grid defines no bulk actions.
 */
export function ExternalBulkActionsBar(props: ExternalBulkActionsBarProps) {
  const t = useTranslationContext();
  const isLoading = isGridDataLoading(props.data);
  const entries = props.data
    ? getBulkActionsFromRows(props.data.rows, props.data.actionsDefinition).filter(
        (entry) =>
          (isNil(props.allowedActions) || props.allowedActions.includes(entry.actionKey)) &&
          !props.removedActions?.includes(entry.actionKey)
      )
    : [];

  if (entries.length === 0) {
    return null;
  }

  return (
    <Dropdown
      placement="bottom-end"
      isDisabled={isLoading}
      dropdownControl={
        <Button
          variant="primary"
          size="default"
          title={props.label ?? t('general.actions.bulkActions', {defaultValue: 'Bulk actions'})}
          rightIcon="hardware/keyboard_arrow_down"
          isDisabled={isLoading}
          data-testid={suffixTestId('externalBulkActions', props)}
        />
      }
      data-testid={suffixTestId('externalBulkActions', props)}
    >
      {entries.map((entry) => (
        <DropdownItem
          key={entry.actionKey}
          label={`${entry.definition.title} (${entry.rowIds.length})`}
          leftIcon={toRegisteredIcon(entry.definition.icon.value)}
          isDisabled={entry.rowIds.length === 0}
          onClick={() =>
            invokeDatagridBulkAction(props.gridCode, {
              actionKey: entry.actionKey,
              rowIds: entry.rowIds,
            })
          }
          data-testid={suffixTestId(`externalBulkAction-${entry.actionKey}`, props)}
        />
      ))}
    </Dropdown>
  );
}
